import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const CATEGORY_COLORS = {
  AUTH: { bg: '#dbeafe', color: '#1e40af' },
  USER: { bg: '#d1fae5', color: '#065f46' },
  LOAD: { bg: '#fef3c7', color: '#92400e' },
  CARRIER: { bg: '#fce7f3', color: '#9d174d' },
  CUSTOMER: { bg: '#e0e7ff', color: '#3730a3' },
  CRM: { bg: '#cffafe', color: '#0e7490' },
  INVOICE: { bg: '#f3e8ff', color: '#7c3aed' },
  FACTORING: { bg: '#fed7aa', color: '#c2410c' },
  ORGANIZATION: { bg: '#fee2e2', color: '#dc2626' },
  SUBSCRIPTION: { bg: '#fef08a', color: '#a16207' },
  PERMISSION: { bg: '#e5e7eb', color: '#374151' },
  SAP: { bg: '#ccfbf1', color: '#0d9488' },
  SYSTEM: { bg: '#f3f4f6', color: '#6b7280' }
};

const ACTION_ICONS = {
  CREATE: '➕',
  UPDATE: '✏️',
  DELETE: '🗑️',
  LOGIN: '🔑',
  LOGOUT: '🚪',
  APPROVE: '✅',
  REJECT: '❌',
  STATUS_CHANGE: '🔄',
  ASSIGN: '👤',
  PAYMENT: '💰',
  SAP_SYNC: '🔄',
  SAP_CONFIG: '⚙️'
};

export default function AuditLog() {
  const { adminEmail } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', action: '', days: 7 });
  const [categories, setCategories] = useState({});

  useEffect(() => {
    loadCategories();
    loadLogs();
    loadStats();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filters.category, filters.action]);

  async function loadCategories() {
    try {
      const res = await fetch('/api/audit/categories');
      const data = await res.json();
      setCategories(data);
    } catch (e) {
      console.error('Load categories error:', e);
    }
  }

  async function loadLogs() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.action) params.append('action', filters.action);
      params.append('limit', '200');
      
      const res = await fetch(`/api/audit/logs?${params}`, {
        headers: { 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' }
      });
      const data = await res.json();
      setLogs(data.logs || []);
    } catch (e) {
      console.error('Load logs error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await fetch(`/api/audit/stats?days=${filters.days}`, {
        headers: { 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' }
      });
      const data = await res.json();
      setStats(data.stats || []);
    } catch (e) {
      console.error('Load stats error:', e);
    }
  }

  function formatDate(dateStr) {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 20 }}>
      <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 900 }}>📋 Audit Log</h1>
      <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>Track all system activity and changes</p>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
        {stats.slice(0, 8).map((stat, i) => {
          const catColor = CATEGORY_COLORS[stat.category] || CATEGORY_COLORS.SYSTEM;
          return (
            <div key={i} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 20, fontWeight: 900 }}>{stat.count}</span>
                <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600, background: catColor.bg, color: catColor.color }}>
                  {stat.category}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{stat.action}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 12 }}>Category</label>
            <select 
              value={filters.category} 
              onChange={e => setFilters({ ...filters, category: e.target.value })}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb', minWidth: 150 }}
            >
              <option value="">All Categories</option>
              {Object.keys(categories.categories || {}).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 12 }}>Action</label>
            <select 
              value={filters.action} 
              onChange={e => setFilters({ ...filters, action: e.target.value })}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb', minWidth: 150 }}
            >
              <option value="">All Actions</option>
              {Object.keys(categories.actions || {}).map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>
          <button 
            onClick={loadLogs}
            style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
          >
            🔄 Refresh
          </button>
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            {logs.length} events
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 90px 90px 1fr 180px 150px', gap: 12, padding: 12, background: '#f9fafb', fontWeight: 700, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>
          <div>Time</div>
          <div>Category</div>
          <div>Action</div>
          <div>Details</div>
          <div>User</div>
          <div>Entity</div>
        </div>
        
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No audit logs found</div>
        ) : (
          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            {logs.map(log => {
              const catColor = CATEGORY_COLORS[log.category] || CATEGORY_COLORS.SYSTEM;
              const actionIcon = ACTION_ICONS[log.action] || '•';
              
              return (
                <div key={log.id} style={{ display: 'grid', gridTemplateColumns: '140px 90px 90px 1fr 180px 150px', gap: 12, padding: 12, borderTop: '1px solid #f3f4f6', alignItems: 'center', fontSize: 13 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(log.createdAt)}</div>
                  <div>
                    <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: catColor.bg, color: catColor.color }}>
                      {log.category}
                    </span>
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {actionIcon} {log.action}
                  </div>
                  <div style={{ color: '#374151' }}>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <details style={{ cursor: 'pointer' }}>
                        <summary style={{ color: '#6b7280', fontSize: 12 }}>View details</summary>
                        <pre style={{ fontSize: 11, background: '#f9fafb', padding: 8, borderRadius: 4, marginTop: 4, overflow: 'auto' }}>
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.userEmail || '—'}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    {log.entityType && (
                      <span>
                        <span style={{ color: '#9ca3af' }}>{log.entityType}:</span>{' '}
                        <span style={{ fontWeight: 500 }}>{log.entityName || log.entityId?.slice(0, 8)}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
